import type { RouteLocationNormalized, Router } from "vue-router";

const OVERLAY_STATE_KEY = "overlayBackGuard";

/**
 * Single, zero-wiring back-button handler for all Vuetify overlays.
 *
 * Vuetify's per-overlay `closeOnBack` is disabled globally (see plugins/vuetify.ts) because it
 * pushes no history entries and shares one flag across overlays — so it can't stack dialogs and
 * fights vue-router when you navigate from inside a dialog. This guard replaces it.
 *
 * Model: a MutationObserver watches managed blocking layers (modal dialogs and temporary
 * navigation drawers) so no component needs to opt in. Every open layer gets one pushed history
 * entry, so one browser-back closes one layer instead of leaving the page. A single invariant is
 * reconciled after each DOM mutation:
 *
 *   pushedOverlayEntries === activeManagedBlockingCount   (on the current route)
 *
 * except while one history.back() is in flight (the counter is held one below active until that
 * back()'s popstate lands, which self-clocks the next pop if more dialogs closed at once).
 */

/** Spare history entries we have pushed for currently-open overlays. */
let pushedOverlayEntries = 0;
/** A history.back() we issued is outstanding; wait for its popstate before issuing the next. */
let popInFlight = false;
/** The next popstate is the result of our own history.back(), not a user gesture. */
let suppressNextPopstate = false;
/**
 * The URL an overlay was closed at, captured right before our cleanup history.back(). If the entry
 * below our spare turns out to be stale (an app-side router.replace retargeted our spare entry —
 * e.g. stripping ?open_choose_tp=1 while the dialog was open), we restore this so the reactive route
 * never re-sees the stale query and re-opens the overlay.
 */
let hrefBeforeBack: string | null = null;
/** Hold reconcile while we dismiss an overlay from popstate/beforeEach (avoid double-counting). */
let suspendReconcile = false;
/**
 * A path change just happened. Old-page overlays may still be tearing down; ignore the active count
 * until they drain, then rebase tracking for the new page. Prevents history.back() from undoing the
 * navigation.
 */
let resyncBaseline = false;
let overlayCountAtRouteNavigation = 0;
/** A back gesture reached us; tell the router guard to dismiss an overlay and block the route. */
let pendingBackNavigation = false;

let observer: MutationObserver | null = null;
let rafScheduled = false;
let popstateHandler: ((event: PopStateEvent) => void) | null = null;
let removeBeforeEach: (() => void) | null = null;
let removeAfterEach: (() => void) | null = null;
let guardInstalled = false;
let syncHistory = true;
let routerRef: Router | null = null;

type OverlayBackGuardOptions = {
  /** When false, skip pushState/history.back (e.g. Storybook — real back breaks Vitest browser). */
  syncHistory?: boolean;
};

function isOverlayHistoryState(state: unknown): boolean {
  return Boolean(
    state && typeof state === "object" && OVERLAY_STATE_KEY in (state as Record<string, unknown>)
  );
}

/** @internal Test and story helpers — keep in sync with `managedOverlays`. */
export function isManagedOverlayElement(overlay: HTMLElement): boolean {
  if (!overlay.classList.contains("v-overlay--active")) return false;
  if (overlay.classList.contains("v-snackbar")) return false;
  if (overlay.classList.contains("back-guard-ignore")) return false;

  // Only modal dialogs — not tooltips, menus, or autocomplete lists (also `.v-overlay--active`).
  const role = overlay.getAttribute("role");
  return role === "dialog" || role === "alertdialog";
}

/**
 * Active overlays that should intercept browser back.
 *
 * - Snackbars never do, matching Vuetify.
 * - Tooltips, menus, and listbox overlays are excluded — they share the page with a dialog and
 *   must not get their own spare history entry (e.g. the close-button tooltip on DialogCross).
 * - `.back-guard-ignore` overlays opt out: these are dialogs opened *by* a route-leave guard in
 *   response to the back gesture itself (e.g. the unsaved-changes confirm in FormActions). The
 *   guard's `next(false)` makes vue-router revert the history position, which fires a second
 *   popstate. If we counted that dialog as managed, this revert-popstate would be read as a fresh
 *   back gesture and dismiss the dialog the instant it opened. Ignoring it keeps the dialog up and
 *   leaves its own buttons (save / discard / close) in charge of navigating.
 */
function managedOverlays(): HTMLElement[] {
  return Array.from(document.querySelectorAll<HTMLElement>(".v-overlay--active")).filter(
    isManagedOverlayElement
  );
}

/** @internal Test helper — temporary mobile drawers with a scrim (nav menu, chat dock list, …). */
export function isManagedDrawerElement(el: HTMLElement): boolean {
  return (
    el.classList.contains("v-navigation-drawer") &&
    el.classList.contains("v-navigation-drawer--temporary") &&
    el.classList.contains("v-navigation-drawer--active")
  );
}

function managedDrawers(): HTMLElement[] {
  return Array.from(
    document.querySelectorAll<HTMLElement>(
      ".v-navigation-drawer.v-navigation-drawer--temporary.v-navigation-drawer--active"
    )
  );
}

function managedBlockingLayers(): HTMLElement[] {
  return [...managedOverlays(), ...managedDrawers()];
}

function managedBlockingCount(): number {
  return managedBlockingLayers().length;
}

/**
 * Wait until blocking overlays are closed and the guard has finished reconciling
 * spare history entries. Call before router.push after closing a Vuetify dialog.
 */
export async function waitForOverlayBackGuardIdle(timeoutMs = 2000): Promise<void> {
  const deadline = Date.now() + timeoutMs;

  while (Date.now() < deadline) {
    if (managedBlockingCount() === 0 && !popInFlight) {
      await new Promise<void>((resolve) => {
        requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
      });

      if (managedBlockingCount() === 0 && !popInFlight) {
        return;
      }
    }

    await new Promise<void>((resolve) => {
      requestAnimationFrame(() => resolve());
    });
  }
}

function topBlockingLayer(): HTMLElement | null {
  const layers = managedBlockingLayers();
  if (layers.length === 0) return null;

  return layers.reduce<HTMLElement>((top, layer) => {
    const z = Number.parseInt(getComputedStyle(layer).zIndex, 10) || 0;
    const topZ = Number.parseInt(getComputedStyle(top).zIndex, 10) || 0;
    return z >= topZ ? layer : top;
  }, layers[0]!);
}

const ESCAPE_KEY_INIT = {
  key: "Escape",
  code: "Escape",
  keyCode: 27,
  which: 27,
  bubbles: true,
  cancelable: true,
} as const;

/**
 * A `persistent` overlay ignores Escape and outside-click outright — Vuetify plays a "can't close"
 * pulse instead (`animateClick`), so both gestures below bounce off it and browser back would do
 * nothing at all. BaseDialog marks such dialogs `.back-guard-closable` when they show a cross, so
 * press that cross instead: it is the same path the user takes, and it runs the host's own close
 * handling (including `guardedClose` prompts) rather than bypassing it.
 */
function dismissViaCloseButton(overlay: HTMLElement): boolean {
  if (!overlay.classList.contains("back-guard-closable")) return false;

  const cross = overlay.querySelector<HTMLElement>('[data-testid="close-dialog-button"]');
  if (!cross) return false;

  cross.click();
  return true;
}

function dismissOverlay(overlay: HTMLElement): void {
  if (dismissViaCloseButton(overlay)) return;

  overlay.dispatchEvent(new KeyboardEvent("keydown", ESCAPE_KEY_INIT));
  document.dispatchEvent(new KeyboardEvent("keydown", ESCAPE_KEY_INIT));

  const scrim = overlay.querySelector(".v-overlay__scrim");
  if (scrim instanceof HTMLElement) {
    // Vuetify's click-outside directive only reacts to a click whose *mousedown* it also saw
    // outside the content (`_clickOutside.lastMousedownWasOutside`). A lone synthetic click is
    // dropped, so send the full press sequence.
    const mouseInit = { bubbles: true, cancelable: true, view: window };
    scrim.dispatchEvent(new MouseEvent("mousedown", mouseInit));
    scrim.dispatchEvent(new MouseEvent("mouseup", mouseInit));
    scrim.dispatchEvent(new MouseEvent("click", mouseInit));
  }
}

function dismissDrawer(drawer: HTMLElement): void {
  document.dispatchEvent(new KeyboardEvent("keydown", ESCAPE_KEY_INIT));

  const root = drawer.parentElement;
  const scrim =
    root?.querySelector(".v-navigation-drawer__scrim") ??
    drawer.closest(".nav-mobile-drawer-layer")?.querySelector(".v-navigation-drawer__scrim");
  if (scrim instanceof HTMLElement) {
    scrim.dispatchEvent(new MouseEvent("click", { bubbles: true, cancelable: true, view: window }));
  }
}

/**
 * Dismiss the topmost blocking layer. Returns the element we tried to close (null if there was
 * none) so the caller can verify it actually went away — a layer can refuse to close (e.g. a
 * `persistent` dialog that only pulses, or an unsaved-changes guard that keeps it up).
 */
function dismissTop(): HTMLElement | null {
  const top = topBlockingLayer();
  if (!top) return null;

  if (isManagedDrawerElement(top)) {
    dismissDrawer(top);
    return top;
  }

  dismissOverlay(top);
  return top;
}

function isLayerStillActive(layer: HTMLElement): boolean {
  if (!layer.isConnected) return false;

  return isManagedDrawerElement(layer) || isManagedOverlayElement(layer);
}

/**
 * A back gesture consumes one spare history entry before we know whether the layer will actually
 * close. When it refuses, put the entry back so the counter still matches what is on screen and the
 * next back press gets a fresh chance instead of falling through to a real navigation.
 *
 * Restoring it with `history.forward()` rather than `pushHistoryEntry()` matters: `pushState`
 * truncates everything ahead of the current position, so a dialog that refuses to close (a
 * `persistent` one only pulses — see VOverlay's `animateClick`) would destroy one real entry per
 * back press and pin the user on the page. The spare we just popped off is still sitting forward of
 * us, so stepping back onto it restores the count without touching the rest of the stack.
 *
 * Two frames, because a dialog that closes behind a confirm prompt (requestClose) needs a tick to
 * settle before `v-overlay--active` drops.
 */
function restoreEntryIfLayerSurvived(layer: HTMLElement | null): void {
  if (!layer || !syncHistory) return;

  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      if (!isLayerStillActive(layer)) return;
      if (managedBlockingCount() <= pushedOverlayEntries) return;

      pushedOverlayEntries += 1;
      popInFlight = true;
      suppressNextPopstate = true;
      hrefBeforeBack = location.href;
      history.forward();
    });
  });
}

function pushHistoryEntry(): void {
  pushedOverlayEntries += 1;
  if (!syncHistory) return;

  history.pushState(
    { ...(history.state as object), [OVERLAY_STATE_KEY]: pushedOverlayEntries },
    "",
    location.href
  );
}

function popOneEntry(): void {
  pushedOverlayEntries -= 1;
  popInFlight = true;
  suppressNextPopstate = true;
  hrefBeforeBack = location.href;
  history.back();
}

/** Keep vue-router aligned when we repair the browser URL after a cleanup history.back(). */
function syncRouterToLocation(): void {
  if (!routerRef || !syncHistory) return;

  const target = `${location.pathname}${location.search}${location.hash}`;
  if (routerRef.currentRoute.value.fullPath === target) return;

  void routerRef.replace(target);
}

/** Reconcile the pushed-entry count with the live overlay count after any DOM mutation. */
function reconcile(): void {
  if (suspendReconcile) return;

  const active = managedBlockingCount();

  if (resyncBaseline) {
    const drained =
      active === 0 || active < overlayCountAtRouteNavigation || overlayCountAtRouteNavigation === 0;
    if (!drained) return;

    resyncBaseline = false;
    overlayCountAtRouteNavigation = 0;
    pushedOverlayEntries = 0;
    popInFlight = false;
    // Fall through and rebase against the new page's current overlays.
  }

  if (active > pushedOverlayEntries) {
    while (pushedOverlayEntries < active) pushHistoryEntry();
    return;
  }

  if (active < pushedOverlayEntries) {
    if (!syncHistory) {
      pushedOverlayEntries = active;
      return;
    }
    if (!popInFlight) popOneEntry();
  }
}

function scheduleReconcile(): void {
  if (rafScheduled) return;
  rafScheduled = true;
  requestAnimationFrame(() => {
    rafScheduled = false;
    reconcile();
  });
}

function handlePopstate(event: PopStateEvent): void {
  if (!guardInstalled) return;

  if (suppressNextPopstate) {
    // This popstate is the echo of our own history.back() (cleanup) or history.forward()
    // (restoring the spare a layer refused to give up). Block Vue Router from seeing it so it
    // doesn't treat our own move as a real navigation (e.g. back to ?open_choose_tp=1).
    event.stopImmediatePropagation();
    suppressNextPopstate = false;
    popInFlight = false;
    // Our spare entry was a duplicate of the route URL when pushed, so stepping across it should be
    // a URL no-op. If it isn't, an app-side router.replace retargeted our spare entry and left the
    // neighbouring entry stale — restore the close-time URL so the dialog can't reopen from it.
    if (hrefBeforeBack !== null && location.href !== hrefBeforeBack) {
      history.replaceState(history.state, "", hrefBeforeBack);
      syncRouterToLocation();
    }
    hrefBeforeBack = null;
    scheduleReconcile();
    return;
  }

  if (managedBlockingCount() > 0) {
    // Real back gesture with a blocking layer open: close the top one and block the route.
    suspendReconcile = true;
    pushedOverlayEntries = Math.max(0, pushedOverlayEntries - 1);
    pendingBackNavigation = true;
    const dismissed = dismissTop();
    queueMicrotask(() => {
      suspendReconcile = false;
    });
    restoreEntryIfLayerSurvived(dismissed);
    return;
  }

  if (isOverlayHistoryState(event.state)) {
    // Landed on a stale overlay entry with nothing open — just drop it from the count.
    pushedOverlayEntries = Math.max(0, pushedOverlayEntries - 1);
  }

  pendingBackNavigation = true;
}

function startOverlayObserver(): void {
  if (observer || typeof document === "undefined") return;

  observer = new MutationObserver(scheduleReconcile);
  observer.observe(document.body, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: ["class"],
  });

  scheduleReconcile();
}

/** @internal Reset module state between unit tests. */
export function resetOverlayBackGuardForTests(): void {
  pushedOverlayEntries = 0;
  popInFlight = false;
  suppressNextPopstate = false;
  hrefBeforeBack = null;
  suspendReconcile = false;
  resyncBaseline = false;
  overlayCountAtRouteNavigation = 0;
  pendingBackNavigation = false;
  rafScheduled = false;
  guardInstalled = false;
  syncHistory = true;
  routerRef = null;
  observer?.disconnect();
  observer = null;

  if (popstateHandler) {
    window.removeEventListener("popstate", popstateHandler, true);
    popstateHandler = null;
  }

  removeBeforeEach?.();
  removeBeforeEach = null;
  removeAfterEach?.();
  removeAfterEach = null;
}

export function installOverlayPopstateListener(): void {
  if (typeof window === "undefined" || popstateHandler) return;

  popstateHandler = handlePopstate;
  window.addEventListener("popstate", popstateHandler, true);
}

export function setupOverlayBackGuard(router: Router, options: OverlayBackGuardOptions = {}): void {
  if (typeof window === "undefined" || guardInstalled) return;

  guardInstalled = true;
  syncHistory = options.syncHistory ?? true;
  routerRef = router;

  startOverlayObserver();
  installOverlayPopstateListener();

  removeBeforeEach = router.beforeEach(() => {
    if (!pendingBackNavigation) return;

    pendingBackNavigation = false;

    const top = topBlockingLayer();
    if (!top) return; // no blocking layer left to close — allow the navigation

    suspendReconcile = true;
    const dismissed = dismissTop();
    queueMicrotask(() => {
      suspendReconcile = false;
    });
    restoreEntryIfLayerSurvived(dismissed);

    return false; // overlay dismissed instead of navigating
  });

  removeAfterEach = router.afterEach(
    (to: RouteLocationNormalized, from: RouteLocationNormalized) => {
      if (to.path === from.path) return; // query/hash-only change: leave overlay tracking alone

      // Forward navigation: vue-router now owns the stack. Drop our spare entries WITHOUT
      // history.back() (that would undo the navigation) and rebase once old overlays drain.
      overlayCountAtRouteNavigation = managedBlockingCount();
      pushedOverlayEntries = 0;
      popInFlight = false;
      resyncBaseline = true;
      scheduleReconcile();
    }
  );
}
